import { useCallback, useMemo, useState } from 'react';
import { splitCollection } from '../../domain/game/carCollection';
import { readCollection, recordWin } from './collection';

/**
 * The reader's own cars, over the catalogue on screen (SPEC-010 PR-10.3).
 *
 * Everything about WHICH cars are open is `domain/game/carCollection`; this hook
 * is the two things that cannot be pure — where the won ids are kept, and the
 * fact that winning one has to move a screen that is already rendered.
 *
 * READ ONCE, on the first render, and held in state afterwards. Not because a
 * storage read is expensive but because the alternative is worse: a value read
 * on every render would make `unlocked` a fresh array every time, and the duel's
 * pool is memoised on exactly that array. A screen that rebuilt its pool sixty
 * times a second would deal a new question on every tick of the clock.
 *
 * It is deliberately NOT a context. Only one screen is mounted at a time — the
 * shell renders one route element — so two live copies of this state cannot
 * disagree, and a provider around the whole app would be a subscription nothing
 * subscribes to.
 *
 * `useCarPrize` is the other half and faces the other way: this hook produces
 * the pool a run is dealt from, that one consumes the run that pool produced.
 * One hook doing both would be a cycle, which is why there are two.
 */
export default function useCarCollection({ cars }) {
    const [wonIds, setWonIds] = useState(readCollection);

    const collection = useMemo(() => splitCollection(cars, wonIds), [cars, wonIds]);

    /**
     * Put a car in the garage.
     *
     * The write happens HERE and the result is handed to `setWonIds` as a value,
     * never from inside an updater: React may call an updater more than once,
     * and Web Storage is not somewhere to find that out. It is the same rule
     * `useDuelRun` banks a finished run under.
     *
     * `recordWin` is idempotent and answers the list it produced, so this never
     * has to decide whether its own write was the one that counted — which
     * matters because strict mode mounts an effect twice in development, and a
     * collection holding the Supra twice would quote the next car a rung too
     * high for ever.
     */
    const award = useCallback((carId) => setWonIds(recordWin(carId)), []);

    return { ...collection, wonIds, award };
}
